/*
 *  (c) 2015 - 2024 ENISCO by FORCAM GmbH
 */

import React, {useContext, useState} from 'react'
import {Box, List, ListItemButton, ListItemText, Typography} from '@mui/material'
import {
  asStaticTranslation,
  AuthContext,
  TranslatedText,
  Translation,
  useLanguage,
  useNotification,
  useResolverEffect
} from '@enisco/mcc-react-lib'
import SwaggerUI from 'swagger-ui-react'
import 'swagger-ui-react/swagger-ui.css'
import {useTheme} from '@mui/material/styles'
import {OpenApi} from '../portal/model/openApis'
import {useIntl} from 'react-intl'
import SearchBar from '../portal/components/menu/SearchBar'
import useMediaQuery from '@mui/material/useMediaQuery'

const OpenApiPage: React.FC = () => {
  const language = useLanguage()
  const [search, setSearch] = useState('')
  const [openApis, setOpenApis] = useState(undefined as undefined | OpenApi[])
  const [shouldLoadOpenApis, setShouldLoadOpenApis] = useState(true)
  const [currentOpenApiUrl, setCurrentOpenApiUrl] = useState<string>('')
  const notification = useNotification()
  const intl = useIntl()
  const theme = useTheme()
  const mdDown = useMediaQuery(theme.breakpoints.down('md'))
  const context = useContext(AuthContext)

  useResolverEffect('mcc-portal-api', '/internal/openapi/all', {
    options: {
      method: 'GET',
      headers: {'Content-Type': 'application/json'},
      cache: 'no-cache'
    },
    onSuccess: async (data) => {
      const json = await data.json()
      const rawOpenApis = json.openApis as OpenApi[]
      rawOpenApis.sort((a, b) =>
        (translateText(language, a.description) ?? '').localeCompare(
          translateText(language, b.description) ?? ''
        )
      )
      setOpenApis(rawOpenApis)
      setShouldLoadOpenApis(false)
    },
    resolveCondition: [shouldLoadOpenApis],
    onError: () =>
      notification(
        'warning',
        intl.formatMessage({
          id: 'OpenApi.NoOpenApiFound',
          defaultMessage: 'No OpenAPI specification was found'
        })
      )
  })

  const compareDescription = (
    name: string | undefined,
    searchString: string
  ) => {
    return name
      ? name.toLocaleLowerCase().includes(searchString.toLocaleLowerCase())
      : false
  }

  const searchResults = (searchString: string) => {
    const searchResult: OpenApi[] = []
    openApis?.forEach((openApi) => {
      if (
        compareDescription(
          translateText(language, openApi.description),
          searchString
        )
      ) {
        searchResult.push(openApi)
      }
    })
    searchResult.sort((a, b) =>
      (translateText(language, a.description) ?? '').localeCompare(
        translateText(language, b.description) ?? ''
      )
    )
    return searchResult
  }

  const getHighlightedText = (
    translationText: Translation,
    highlight: string
  ) => {
    const text = translateText(language, translationText)
    if (text) {
      const regex = `(${highlight})`.replace('?', '\\?')
      const parts = text.split(new RegExp(regex, 'gi'))
      return (
        <span>
          {parts.map((part) => (
            <Box component={'span'} key={part}>
              {part}
            </Box>
          ))}
        </span>
      )
    }
  }

  const translateText = (
    languageKey: string,
    text?: Translation
  ): string | undefined => {
    const staticTranslation = asStaticTranslation(text)
    if (staticTranslation) {
      return staticTranslation['default'] ? staticTranslation['default'] : staticTranslation[languageKey]
    }

    return undefined
  }

  const requestInterceptor = (req: any) => (
    {
      ...req,
      headers: {
        Authorization: `Bearer ${context.keycloak?.token}`
      }
    }
  );

  return (
    <Box>
      <Box
        sx={{
          top: 0,
          float: 'left',
          position: 'fixed',
          width: '20%',
          height: '100vh',
          maxHeight: '100%',
          overflow: 'auto'
        }}
      >
        <SearchBar
          value={search}
          mdDown={mdDown}
          onChange={(searchString: string) => setSearch(searchString)}
        />
        {search ? (
          <List>
            {searchResults(search).map((openApi) => (
              <ListItemButton
                sx={{paddingLeft: 4}}
                onClick={() => setCurrentOpenApiUrl(openApi.url)}
                key={openApi.service + '_' + openApi.url}
              >
                <ListItemText
                  primary={
                    <Typography variant='body1'>
                      {getHighlightedText(openApi.description, search)}
                    </Typography>
                  }
                  secondary={
                    <Typography variant='caption'>{openApi.service}</Typography>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        ) : (
          <List component='div' disablePadding>
            {openApis?.map((openApi) => (
                <ListItemButton
                  sx={{paddingLeft: 4}}
                  onClick={() => setCurrentOpenApiUrl(openApi.url)}
                  key={openApi.service + '_' + openApi.url}
                >
                  <ListItemText
                    primary={
                      <Typography variant='body1'>
                        <TranslatedText text={openApi.description} />
                      </Typography>
                    }
                    secondary={
                      <Typography variant='caption'>
                        {openApi.service}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
          </List>
        )}
      </Box>
      <Box
        sx={{
          float: 'left',
          overflow: 'auto',
          left: '20%',
          width: '80%',
          position: 'absolute',
          backgroundColor: 'white' //default swagger ui background
        }}
      >
        {currentOpenApiUrl ? (
          <SwaggerUI
            url={currentOpenApiUrl}
            requestInterceptor={requestInterceptor}
          />
        ) : undefined}
      </Box>
    </Box>
  )
}

export default OpenApiPage
